import React, {
  BaseSyntheticEvent,
  useCallback,
  useMemo,
  useState,
} from 'react';
import { Box, Checkbox, Typography } from '@mui/material';
import { ReportsToolbox } from './reportsToolbox';
import { AppRow } from '../../layout/appRow';
import { ColorPalette, TypographyFamily } from '../../styles/constants';
import { FieldValues } from 'react-hook-form';
import { ReportsEmployeeList } from './reportsEmployeeList';
import { ReportsProjectList } from './reportsProjectList';
import {
  useReportsFilters,
  useReportsFiltersDispatch,
  useReportsViewMode,
} from '../../context/reports/useReportsCtx';
import { FiltersFormData, ViewMode } from '../../models/reports';
import { useAppDispatch } from '../../hooks';
import { setEmployeeCardState } from '../../store/reports/reportsSlice';

const styles = {
  allocationsRow: {
    pr: 2,
    mb: 2,
    justifyContent: 'flex-end',
    alignItems: 'end',
    gap: 0.5,
  },
  listRow: {
    mb: 2,
    display: 'flex',
    flexDirection: 'column',
  },
  allocationsBtn: {
    textDecorationColor: ColorPalette.GREEN,
    fontFamily: TypographyFamily.NEXA,
    marginLeft: '16px',
  },
  checkboxContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: 0.5,
  },
};

interface ReportsPageLargeLayoutProps {
  handleDisplay: (data: FieldValues) => void;
  handleUnloggedProjectsCheck?: (viewUnloggedProjects: boolean) => void;
}

export function ReportsPageLargeLayout({
  handleUnloggedProjectsCheck,
  ...props
}: ReportsPageLargeLayoutProps): JSX.Element {
  const [viewUnloggedEmployees, setViewUnloggedEmployees] = useState(false);
  const [viewUnloggedProjects, setViewUnloggedProjects] = useState(true);

  const filters = useReportsFilters();
  const dispatchFilters = useReportsFiltersDispatch();
  const dispatch = useAppDispatch();

  const [viewMode] = useReportsViewMode();

  const isUnloggedEmployeesAvailable = useMemo(
    () => !filters?.projectId && viewMode === ViewMode.PEOPLE,
    [filters?.projectId, viewMode]
  );
  const isUnloggedProjectsAvailable = useMemo(
    () => viewMode === ViewMode.PROJECT,
    [viewMode]
  );

  const handleViewUnloggedEmployees = useCallback((e: BaseSyntheticEvent) => {
    setViewUnloggedEmployees(e.target.checked);
  }, []);

  const handleViewDirectSubordinates = useCallback(
    (e: BaseSyntheticEvent) => {
      dispatchFilters({ ...filters, showHierarchy: e.target.checked });
    },
    [dispatchFilters, filters]
  );

  const handleViewShowInactiveEmployees = useCallback(
    (e: BaseSyntheticEvent) => {
      dispatchFilters({ ...filters, includeInactive: e.target.checked });
    },
    [dispatchFilters, filters]
  );

  const handleViewUnloggedProjects = useCallback(
    (e: BaseSyntheticEvent) => {
      if (handleUnloggedProjectsCheck) {
        handleUnloggedProjectsCheck(e.target.checked);
      }
      setViewUnloggedProjects(e.target.checked);
    },
    [handleUnloggedProjectsCheck]
  );

  const handleDisplay = useCallback(
    (data: FiltersFormData) => {
      dispatch(setEmployeeCardState({ employeeId: '' }));
      if (data?.project?.id) {
        setViewUnloggedEmployees(false);
      }

      props.handleDisplay(data);
    },
    [dispatch, props]
  );

  return (
    <>
      <Box id="toolbox">
        <ReportsToolbox
          handleDisplay={handleDisplay}
          viewUnloggedProjects={viewUnloggedProjects}
        />
      </Box>

      <Box id="allocations">
        <AppRow sx={styles.allocationsRow}>
          {viewMode === ViewMode.PEOPLE && (
            <>
              <Box sx={styles.checkboxContainer}>
                <Typography>Show indirect employees</Typography>
                <Checkbox
                  onChange={handleViewDirectSubordinates}
                  checked={filters?.showHierarchy || false}
                />
              </Box>

              <Box sx={styles.checkboxContainer}>
                <Typography>Show also inactive</Typography>
                <Checkbox
                  onChange={handleViewShowInactiveEmployees}
                  checked={filters?.includeInactive || false}
                />
              </Box>

              {isUnloggedEmployeesAvailable && (
                <Box sx={styles.checkboxContainer}>
                  <Typography>Only discrepancies</Typography>
                  <Checkbox
                    onChange={handleViewUnloggedEmployees}
                    checked={viewUnloggedEmployees}
                  />
                </Box>
              )}
            </>
          )}
          {isUnloggedProjectsAvailable && (
            <Box sx={styles.checkboxContainer}>
              <Typography>Show only projects with logged time</Typography>
              <Checkbox
                onChange={handleViewUnloggedProjects}
                checked={viewUnloggedProjects}
              />
            </Box>
          )}
        </AppRow>
      </Box>

      {filters && (
        <AppRow sx={styles.listRow}>
          {viewMode === ViewMode.PEOPLE ? (
            <ReportsEmployeeList
              viewUnloggedEmployees={viewUnloggedEmployees}
              viewShowInactiveEmployees={filters.includeInactive || false}
            />
          ) : (
            <ReportsProjectList viewUnloggedProjects={viewUnloggedProjects} />
          )}
        </AppRow>
      )}
    </>
  );
}
